import { z } from 'zod'

import { pagedSchema } from '../core'

const text = z.string().nullish()

/**
 * A template variation the server made for a project once its design was approved: one or more pictures (`artworkUrls`, one for each
 * page, as paths in the server's storage, downloaded like any generated picture), the instructions it was made from, and where it stands.
 * The server numbers them (`variationNumber`), but the number carries no meaning for a person, so the app never shows it.
 */
export const templateVersionSchema = z.object({
  id: z.string(),
  projectId: text,
  variationNumber: z.number().nullish(),
  artworkUrls: z.array(z.string()).nullish().transform((paths) => (paths ?? []).filter((path) => path.trim() !== '')),
  instructions: text,
  logoUrl: text,
  /** The content the version was made from: the server sends it as a JSON object (older rows may hold it as JSON text). */
  requirementContent: z.unknown().optional(),
  status: text,
  createdBy: text,
  approvedBy: text,
  approvedAt: text,
  createdAt: text,
  updatedAt: text,
})
export type TemplateVersionDto = z.infer<typeof templateVersionSchema>

export const templateVersionPageSchema = pagedSchema(templateVersionSchema)

export interface TemplateVersionFilters {
  projectId?: string
  createdBy?: string
  status?: string
}

/** The answer to creating a version: the version (its pictures come when the task is done), and the background task that makes them. */
export const createTemplateVersionResponseSchema = z.object({
  templateVersion: templateVersionSchema,
  taskId: text,
  taskStatus: text,
  progress: z.number().nullish(),
  model: text,
  eventsUrl: text,
})
export type CreateTemplateVersionResponseDto = z.infer<typeof createTemplateVersionResponseSchema>

/**
 * What one variation is made from (`POST /api/projects/{id}/templates/generate-variations`, JSON): who asks, the complete requirement
 * content (the menu with every item's details, as the requirement analysis holds it, with any edits), the master template's pictures
 * (server paths), and, optionally, words about the look (bold headings, where the prices sit). No logo: the call takes JSON only.
 */
export interface GenerateVariationBody {
  createdBy: string
  content: Record<string, unknown>
  imageUrl: string[]
  instructions?: string
}
